import React, { useEffect, useRef, useState } from 'react';
import { loadGoogleMapsScript } from '../services/GoogleMap';

// Dubai, used when no location is set yet
const DEFAULT_CENTER = { lat: 25.2048, lng: 55.2708 };

const hasCoords = (lat, lng) =>
  lat !== null && lat !== undefined && lat !== '' && lng !== null && lng !== undefined && lng !== '' &&
  !isNaN(Number(lat)) && !isNaN(Number(lng));

/**
 * Address search (Google Places autocomplete) + map with a draggable marker.
 * Picking a suggestion moves the marker; clicking / dragging on the map moves
 * the marker and reverse-geocodes the address into the input.
 * onChange receives { location, lat, lng }.
 */
const LocationPicker = ({ location, lat, lng, onChange, placeholder = 'Search location' }) => {
  const inputRef = useRef(null);
  const mapRef = useRef(null);
  const map = useRef(null);
  const marker = useRef(null);
  const geocoder = useRef(null);
  const onChangeRef = useRef(onChange);
  const [error, setError] = useState('');

  onChangeRef.current = onChange;

  // Init map, marker and autocomplete once
  useEffect(() => {
    let cancelled = false;
    const mapKey = localStorage.getItem('map_key') || process.env.REACT_APP_GOOGLE_MAP_KEY;
    if (!mapKey) {
      setError('Google Maps API key is missing');
      return;
    }

    loadGoogleMapsScript(mapKey)
      .then((gmaps) => {
        if (cancelled || !mapRef.current) return;
        const start = hasCoords(lat, lng) ? { lat: Number(lat), lng: Number(lng) } : DEFAULT_CENTER;

        map.current = new gmaps.Map(mapRef.current, {
          center: start,
          zoom: hasCoords(lat, lng) ? 15 : 11,
          mapTypeControl: false,
          streetViewControl: false,
        });
        marker.current = new gmaps.Marker({
          map: map.current,
          position: start,
          draggable: true,
          visible: hasCoords(lat, lng),
        });
        geocoder.current = new gmaps.Geocoder();

        const pickPoint = (latLng) => {
          marker.current.setPosition(latLng);
          marker.current.setVisible(true);
          const point = { lat: latLng.lat(), lng: latLng.lng() };
          geocoder.current.geocode({ location: point }, (results, status) => {
            const address = status === 'OK' && results[0] ? results[0].formatted_address : inputRef.current.value;
            onChangeRef.current({ location: address, ...point });
          });
        };

        map.current.addListener('click', (e) => pickPoint(e.latLng));
        marker.current.addListener('dragend', (e) => pickPoint(e.latLng));

        const autocomplete = new gmaps.places.Autocomplete(inputRef.current, {
          fields: ['formatted_address', 'geometry', 'name'],
          componentRestrictions: { country: 'ae' },
        });
        autocomplete.bindTo('bounds', map.current);
        autocomplete.addListener('place_changed', () => {
          const place = autocomplete.getPlace();
          if (!place.geometry) return;
          if (place.geometry.viewport) map.current.fitBounds(place.geometry.viewport);
          else map.current.setCenter(place.geometry.location);
          map.current.setZoom(Math.max(map.current.getZoom(), 15));
          marker.current.setPosition(place.geometry.location);
          marker.current.setVisible(true);
          onChangeRef.current({
            location: place.formatted_address || place.name,
            lat: place.geometry.location.lat(),
            lng: place.geometry.location.lng(),
          });
        });
      })
      .catch(() => setError('Failed to load Google Maps'));

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sync marker when coords arrive later (e.g. edit form loaded after mount)
  useEffect(() => {
    if (!map.current || !marker.current || !hasCoords(lat, lng)) return;
    const pos = { lat: Number(lat), lng: Number(lng) };
    const cur = marker.current.getPosition();
    if (marker.current.getVisible() && cur && cur.lat() === pos.lat && cur.lng() === pos.lng) return;
    marker.current.setPosition(pos);
    marker.current.setVisible(true);
    map.current.setCenter(pos);
    map.current.setZoom(15);
  }, [lat, lng]);

  return (
    <div>
      <input
        ref={inputRef}
        type="text"
        className="form-control form-control-solid mb-3"
        placeholder={placeholder}
        value={location || ''}
        onChange={(e) => onChange({ location: e.target.value, lat, lng })}
        // Enter selects a suggestion instead of submitting the form
        onKeyDown={(e) => e.key === 'Enter' && e.preventDefault()}
      />
      {error && <div className="text-danger fs-7 mb-2">{error}</div>}
      <div ref={mapRef} style={{ width: '100%', height: 320, borderRadius: 8 }} />
      <div className="form-text">
        Search an address or click / drag the marker on the map.
        {hasCoords(lat, lng) && <> &nbsp;Lat: {Number(lat).toFixed(6)}, Lng: {Number(lng).toFixed(6)}</>}
      </div>
    </div>
  );
};

export default LocationPicker;
